import React, { useState, useEffect } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Alert, Linking } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { WorkStackParamList } from '../../types/navigation';
import { useWork } from '../../context/WorkContext';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../../components/common/ThemedText';
import { Job, QuoteAttachmentPayload } from '../../types/work';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { storage } from '../../utils/storage';
import { buildApiUrl } from '../../config/env';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';

type SendQuoteNavigationProp = StackNavigationProp<WorkStackParamList, 'SendQuote'>;
type SendQuoteRouteProp = RouteProp<WorkStackParamList, 'SendQuote'>;

const SendQuoteScreen: React.FC = () => {
  const navigation = useNavigation<SendQuoteNavigationProp>();
  const route = useRoute<SendQuoteRouteProp>();
  const { theme } = useTheme();
  const { fetchJobById, submitQuote } = useWork();
  const { jobId, job: initialJob } = route.params;
  const { bottom: bottomInset } = useSafeAreaInsets();

  console.log('🔍 [SendQuoteScreen] bottomInset:', bottomInset);

  const [job, setJob] = useState<Job | null>(initialJob || null);
  const [loading, setLoading] = useState(!initialJob);
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState<QuoteAttachmentPayload | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [errors, setErrors] = useState({ amount: false });
  const [headerHeight, setHeaderHeight] = useState(60);
  const { keyboardHeight, isKeyboardVisible } = useKeyboardHeight();

  useEffect(() => {
    // Only fetch if we don't have initial job data
    if (!initialJob) {
      loadJob();
    }
  }, [jobId, initialJob]);

  useEffect(() => {
    console.log('🔍 [SendQuoteScreen] Bottom spacing:', bottomInset + headerHeight, '(bottomInset:', bottomInset, '+ headerHeight:', headerHeight, ') | isKeyboardVisible:', isKeyboardVisible, 'keyboardSpacer:', Math.max(0, keyboardHeight - bottomInset));
  }, [bottomInset, isKeyboardVisible, keyboardHeight, headerHeight]);

  const loadJob = async () => {
    setLoading(true);
    const jobData = await fetchJobById(jobId);
    setJob(jobData);
    setLoading(false);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) {
      return 'PDF';
    }

    const kb = bytes / 1024;
    if (kb >= 1024) {
      return `${(kb / 1024).toFixed(1)} MB`;
    }
    return `${Math.max(kb, 0.1).toFixed(1)} KB`;
  };

  const handleAttachPdf = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets?.[0];
      if (!file) {
        return;
      }

      if (file.size && file.size > 10 * 1024 * 1024) {
        Alert.alert('File Too Large', 'Please select a PDF smaller than 10MB.');
        return;
      }

      const token = await storage.getToken();
      if (!token) {
        showErrorToast('Please sign in again to upload attachments.');
        return;
      }

      setUploadingAttachment(true);

      const formData = new FormData();
      formData.append('file', {
        uri: file.uri,
        name: file.name || `quote-attachment-${Date.now()}.pdf`,
        type: file.mimeType || 'application/pdf',
      } as any);

      const response = await fetch(buildApiUrl('api/upload/pdf'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success || !data.url) {
        throw new Error(data.message || 'Failed to upload PDF');
      }

      setAttachment({
        url: data.url,
        name: file.name || data.filename || 'quotation.pdf',
        size: file.size ?? data.size,
      });

      showSuccessToast('PDF attached');
    } catch (error) {
      console.error('Error attaching PDF:', error);
      showErrorToast('Failed to attach the PDF. Please try again.');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
  };

  const handleOpenAttachment = () => {
    if (attachment?.url) {
      Linking.openURL(attachment.url).catch(() => {
        Alert.alert('Unable to open PDF', 'Please try again later.');
      });
    }
  };

  const handleSubmitQuote = async () => {
    // Reset errors
    setErrors({ amount: false });

    // Validation
    if (!amount.trim()) {
      setErrors({ amount: true });
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setErrors({ amount: true });
      return;
    }

    if (uploadingAttachment) {
      showErrorToast('Please wait for the PDF upload to finish.');
      return;
    }

    setSubmitting(true);
    try {
      await submitQuote(jobId, numericAmount, message, attachment || undefined);
      
      // Show success toast notification
      showSuccessToast('Quote submitted successfully!');
      
      // Navigate back to work home after a brief delay
      setTimeout(() => {
        navigation.navigate('WorkHome');
      }, 100);
    } catch (error) {
      showErrorToast('Failed to submit quote. Please try again.');
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!job) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
            <Ionicons name="close" size={28} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Send Quote</ThemedText>
          <View style={styles.headerButton} />
        </View>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={theme.textSecondary} />
          <ThemedText style={[styles.errorText, { color: theme.textSecondary }]}>Job not found</ThemedText>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View 
        style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}
          onLayout={(event) => {
            const { height } = event.nativeEvent.layout;
            if (height !== headerHeight) {
              setHeaderHeight(height);
            }
          }}
      >
        <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Send Quote</ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView 
        style={styles.content} 
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[styles.jobSummary, { backgroundColor: theme.backgroundSecondary }]}>
          <ThemedText style={[styles.jobLabel, { color: theme.textSecondary }]}>Sending quote for</ThemedText>
          <ThemedText style={[styles.jobTitle, { color: theme.text }]}>{job.title}</ThemedText>
          
          <View style={styles.jobMeta}>
            <View style={[styles.categoryBadge, { backgroundColor: theme.backgroundTertiary }]}>
              <ThemedText style={[styles.categoryText, { color: theme.accent }]}>{job.category}</ThemedText>
            </View>
            {job.budget && (
              <ThemedText style={[styles.jobBudget, { color: theme.accent }]}>Budget: ${job.budget.toLocaleString()}</ThemedText>
            )}
          </View>
          
          {job.description && (
            <View style={styles.descriptionBox}>
              <ThemedText style={[styles.descriptionLabel, { color: theme.textSecondary }]}>Description</ThemedText>
              <ThemedText style={[styles.descriptionText, { color: theme.text }]} numberOfLines={3}>{job.description}</ThemedText>
            </View>
          )}
        </View>

        <View style={[styles.form, { backgroundColor: theme.backgroundSecondary }]}>
          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Your Quote *</ThemedText>
            <View style={[
              styles.amountInputContainer, 
              { backgroundColor: theme.backgroundTertiary },
              errors.amount && { borderWidth: 2, borderColor: theme.error }
            ]}>
              <ThemedText style={[styles.currencySymbol, { color: theme.text }]}>$</ThemedText>
              <TextInput
                style={[styles.amountInput, { color: theme.text }]}
                placeholder="0"
                value={amount}
                onChangeText={(text) => {
                  setAmount(text);
                  if (errors.amount) setErrors({ amount: false });
                }}
                keyboardType="numeric"
                placeholderTextColor={theme.textSecondary}
              />
            </View>
            {errors.amount && (
              <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
            )}
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Message (Optional)</ThemedText>
            <TextInput
              style={[styles.input, styles.textArea, { backgroundColor: theme.backgroundTertiary, color: theme.text }]}
              placeholder="Tell the client why you're the best fit for this job..."
              value={message}
              onChangeText={setMessage}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          <View style={styles.formGroup}>
            <View style={styles.attachmentHeader}>
              <ThemedText style={[styles.label, { color: theme.text }]}>PDF Attachment</ThemedText>
              {attachment && (
                <TouchableOpacity onPress={handleRemoveAttachment} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <ThemedText style={[styles.removeAttachmentText, { color: theme.error }]}>Remove</ThemedText>
                </TouchableOpacity>
              )}
            </View>

            {attachment ? (
              <View
                style={[
                  styles.attachmentPreview,
                  { backgroundColor: theme.backgroundTertiary, borderColor: theme.border },
                ]}
              >
                <View style={[styles.attachmentIcon, { backgroundColor: theme.accent + '22' }]}>
                  <Ionicons name="document-text-outline" size={24} color={theme.accent} />
                </View>
                <View style={styles.attachmentDetails}>
                  <ThemedText style={[styles.attachmentName, { color: theme.text }]} numberOfLines={2}>
                    {attachment.name}
                  </ThemedText>
                  <ThemedText style={[styles.attachmentMeta, { color: theme.textSecondary }]}>
                    {formatFileSize(attachment.size)}
                  </ThemedText>
                </View>
                <TouchableOpacity onPress={handleOpenAttachment} style={styles.attachmentAction}>
                  <Ionicons name="open-outline" size={22} color={theme.accent} />
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                style={[
                  styles.attachmentPicker,
                  { backgroundColor: theme.backgroundTertiary, borderColor: theme.border },
                ]}
                onPress={handleAttachPdf}
                disabled={uploadingAttachment}
                activeOpacity={0.85}
              >
                <View style={styles.attachmentPickerContent}>
                  {uploadingAttachment ? (
                    <>
                      <ActivityIndicator size="small" color={theme.accent} />
                      <ThemedText style={[styles.attachmentPickerText, { color: theme.textSecondary }]}>Uploading PDF...</ThemedText>
                    </>
                  ) : (
                    <>
                      <Ionicons name="document-attach-outline" size={24} color={theme.accent} />
                      <View style={styles.attachmentPickerTexts}>
                        <ThemedText style={[styles.attachmentPickerText, { color: theme.text }]}>Attach PDF</ThemedText>
                        <ThemedText style={[styles.attachmentHelperText, { color: theme.textSecondary }]}>Only .pdf files up to 10MB</ThemedText>
                      </View>
                    </>
                  )}
                </View>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity 
            style={[styles.cancelButton, { borderColor: theme.border }]} 
            onPress={handleCancel}
            disabled={submitting || uploadingAttachment}
          >
            <ThemedText
              style={[
                styles.cancelButtonText,
                { color: submitting || uploadingAttachment ? theme.textTertiary : theme.textSecondary },
              ]}
            >
              Cancel
            </ThemedText>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[
              styles.submitButton,
              {
                backgroundColor: submitting || uploadingAttachment ? theme.textSecondary : theme.accent,
                opacity: submitting || uploadingAttachment ? 0.7 : 1,
              },
            ]} 
            onPress={handleSubmitQuote}
            disabled={submitting || uploadingAttachment}
            activeOpacity={submitting || uploadingAttachment ? 1 : 0.7}
          >
            {submitting ? (
              <ActivityIndicator size="small" color={theme.textInverse} />
            ) : uploadingAttachment ? (
              <ThemedText style={[styles.submitButtonText, { color: theme.textInverse }]}>Uploading PDF...</ThemedText>
            ) : (
              <ThemedText style={[styles.submitButtonText, { color: theme.textInverse }]}>Submit Quote</ThemedText>
            )}
          </TouchableOpacity>
        </View>
        
        <FormFooterSpacer
          headerHeight={headerHeight}
          bottomInset={bottomInset}
          keyboardHeight={keyboardHeight}
        />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorText: {
    fontSize: 16,
    marginTop: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  jobSummary: {
    padding: 16,
    marginBottom: 12,
  },
  jobLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  jobBudget: {
    fontSize: 14,
    fontWeight: '600',
  },
  jobMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
  },
  categoryBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  categoryText: {
    fontSize: 12,
    fontWeight: '600',
  },
  descriptionBox: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
  },
  descriptionLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: 20,
  },
  form: {
    padding: 16,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  amountInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
  },
  currencySymbol: {
    fontSize: 20,
    fontWeight: '600',
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    paddingVertical: 16,
    fontSize: 20,
    fontWeight: '600',
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  attachmentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  removeAttachmentText: {
    fontSize: 12,
    fontWeight: '600',
  },
  attachmentPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    gap: 12,
  },
  attachmentIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  attachmentDetails: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 14,
    fontWeight: '600',
  },
  attachmentMeta: {
    fontSize: 12,
    marginTop: 4,
  },
  attachmentAction: {
    padding: 6,
  },
  attachmentPicker: {
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  attachmentPickerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  attachmentPickerTexts: {
    flex: 1,
  },
  attachmentPickerText: {
    fontSize: 14,
    fontWeight: '600',
  },
  attachmentHelperText: {
    fontSize: 12,
    marginTop: 2,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
    paddingHorizontal: 16,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  submitButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default SendQuoteScreen;
